import { dcLoan, dcPayment, dcAffordability, dcDti, dcCostOfOwnership } from "@/lib/api";
import { fmtMoney, fmtPct, num } from "@/lib/format";

const dealFields = [
  { key: "price", label: "Vehicle price", type: "currency", def: "45000" },
  { key: "down", label: "Down payment", type: "currency", def: "5000" },
  { key: "trade", label: "Trade-in equity", type: "currency", def: "0", help: "Negative if you owe more than it's worth" },
  { key: "term", label: "Loan term", type: "term", def: "72" },
  { key: "creditScore", label: "Credit score range", type: "credit", def: "700", help: "DriveClear estimates the APR for your tier" },
];

const dealPayload = (v) => ({
  vehicle_price: num(v.price),
  down_payment: num(v.down),
  trade_equity: num(v.trade),
  term_months: parseInt(v.term, 10),
  credit_score: parseInt(v.creditScore, 10),
});

const financeFields = [
  { key: "income", label: "Monthly income (before tax)", type: "currency", def: "8000" },
  { key: "debts", label: "Monthly debts", type: "currency", def: "600", help: "Cards, student loans, other payments" },
  { key: "housing", label: "Housing cost", type: "currency", def: "2000", help: "Rent or mortgage" },
];

const affordabilityPayload = (v) => ({
  monthly_income: num(v.income),
  monthly_debts: num(v.debts),
  housing_cost: num(v.housing),
  credit_score: parseInt(v.creditScore, 10),
  down_payment: num(v.down),
  trade_equity: 0,
  term_months: parseInt(v.term, 10),
});

export const CALCULATOR_PAGES = [
  {
    path: "/car-payment-calculator",
    slug: "car-payment-calculator",
    title: "Car Payment Calculator — Estimate Your Monthly Payment | DriveClear",
    description: "Calculate your estimated monthly car payment from vehicle price, down payment, trade-in, term and credit tier. Free, no account, powered by the DriveClear engine.",
    h1: "Car Payment Calculator",
    intro: "Enter the deal as advertised and see the estimated monthly payment — including the taxes and fees most calculators leave out.",
    ctaLabel: "Calculate Payment",
    fields: dealFields,
    compute: async (v) => {
      const { data } = await dcPayment(dealPayload(v));
      return {
        primary: { label: "Estimated payment", value: `${fmtMoney(data.monthly_payment)}/mo` },
        tiles: [
          { label: "Amount financed", value: fmtMoney(data.amount_financed) },
          { label: "Estimated APR", value: fmtPct(data.apr) },
          { label: "Term", value: `${data.term_months} months` },
        ],
        note: "Estimate includes DriveClear's standard 10% allowance for taxes, doc and registration fees rolled into financing.",
      };
    },
    faqs: [
      { q: "Is this payment exact?", a: "It's an educational estimate. Taxes and fees are estimated at 10% of price, and your actual APR depends on the lender. The estimate is produced by the same DriveClear engine the app uses." },
      { q: "Does checking a payment affect my credit?", a: "No. This calculator never touches your credit. No account, no SSN, no credit pull." },
    ],
    related: ["/car-loan-calculator", "/car-affordability-calculator", "/car-cost-of-ownership"],
  },
  {
    path: "/car-loan-calculator",
    slug: "car-loan-calculator",
    title: "Car Loan Calculator — Principal, Interest & Total Cost | DriveClear",
    description: "See the full picture of an auto loan: amount financed, monthly payment, total interest and total borrowing cost over the life of the loan.",
    h1: "Car Loan Calculator",
    intro: "A car loan is more than a payment. See how much you actually borrow, what interest adds up to, and the true total cost of the vehicle.",
    ctaLabel: "Calculate Loan",
    fields: dealFields,
    compute: async (v) => {
      const { data } = await dcLoan(dealPayload(v));
      return {
        primary: { label: "Monthly payment", value: `${fmtMoney(data.monthly_payment)}/mo` },
        tiles: [
          { label: "Amount financed", value: fmtMoney(data.amount_financed) },
          { label: "Total interest", value: fmtMoney(data.total_interest) },
          { label: "Total cost", value: fmtMoney(data.total_cost) },
        ],
        viz: { type: "splitBar", principal: data.amount_financed, interest: data.total_interest },
        note: `Includes an estimated ${fmtMoney(data.taxes_and_fees)} in taxes, doc and registration fees rolled into financing.`,
      };
    },
    faqs: [
      { q: "What's the difference between total of payments and total cost?", a: "Total of payments is everything you pay the lender over the term. Total cost adds your cash down payment on top — the real all-in price of the car." },
      { q: "Why is my financed amount higher than price minus down payment?", a: "Taxes, doc and registration fees are typically rolled into the loan. DriveClear estimates these at 10% of the vehicle price." },
    ],
    related: ["/car-payment-calculator", "/auto-loan-calculator", "/dti-calculator"],
  },
  {
    path: "/auto-loan-calculator",
    slug: "auto-loan-calculator",
    title: "Auto Loan Calculator — Compare Loan Terms Side by Side | DriveClear",
    description: "Compare 36 to 84 month auto loan terms side by side: see how each term changes your monthly payment and total interest paid.",
    h1: "Auto Loan Term Comparison",
    intro: "Longer terms shrink the payment but grow the interest. Compare every common term on the same deal and pick with your eyes open.",
    ctaLabel: "Compare Terms",
    fields: dealFields.filter((f) => f.key !== "term"),
    compute: async (v) => {
      const base = dealPayload(v);
      const rows = await Promise.all(
        [36, 48, 60, 72, 84].map(async (t) => {
          const { data } = await dcLoan({ ...base, term_months: t });
          return { term: t, payment: data.monthly_payment, interest: data.total_interest };
        })
      );
      const mid = rows.find((r) => r.term === 60);
      return {
        primary: { label: "60-month payment", value: `${fmtMoney(mid.payment)}/mo` },
        tiles: [],
        viz: { type: "termTable", rows },
        note: "Shorter terms cost less interest but raise the monthly payment. Longer terms do the opposite — and can leave you owing more than the car is worth.",
      };
    },
    faqs: [
      { q: "Is a longer loan term bad?", a: "Not always, but it raises total interest and the risk of negative equity. Compare the total interest column — the difference between 60 and 84 months is often thousands of dollars." },
      { q: "Which term should I choose?", a: "One where the payment fits your finances comfortably. After comparing here, run the DriveClear Fit Check on the homepage to see how the payment fits your full budget." },
    ],
    related: ["/car-loan-calculator", "/car-payment-calculator", "/dti-calculator"],
  },
  {
    path: "/car-affordability-calculator",
    slug: "car-affordability-calculator",
    title: "Car Affordability Calculator — What Price Car Can I Afford? | DriveClear",
    description: "Estimate a comfortable vehicle price range from your income, debts, housing and credit — computed by the real DriveClear engine, not a rule of thumb.",
    h1: "Car Affordability Calculator",
    intro: "Instead of picking a car and hoping, start with your finances and get a price range. Comfortable means the vehicle still scores a strong DriveClear Fit.",
    ctaLabel: "Find My Range",
    fields: [
      ...financeFields,
      { key: "creditScore", label: "Credit score range", type: "credit", def: "700" },
      { key: "down", label: "Down payment", type: "currency", def: "5000" },
      { key: "term", label: "Loan term", type: "term", def: "72" },
    ],
    compute: async (v) => {
      const { data } = await dcAffordability(affordabilityPayload(v));
      if (!data.comfortable_vehicle_price && !data.max_vehicle_price) {
        return {
          primary: { label: "Comfortable range", value: "$0" },
          tiles: [],
          note: "At these numbers, no vehicle price clears the comfortable bar. Try lowering monthly debts or raising the down payment — the app can show you exactly which move helps most.",
        };
      }
      return {
        primary: { label: "Comfortable vehicle price", value: fmtMoney(data.comfortable_vehicle_price) },
        tiles: [
          { label: "Max a lender may work with", value: fmtMoney(data.max_vehicle_price) },
          { label: "Comfortable payment", value: `${fmtMoney(data.comfortable_monthly_payment)}/mo` },
          { label: "Estimated APR", value: fmtPct(data.apr) },
        ],
        viz: { type: "affordRange", comfortable: data.comfortable_vehicle_price, max: data.max_vehicle_price },
        note: data.note,
      };
    },
    faqs: [
      { q: "What's the difference between comfortable and max?", a: "Comfortable is the most expensive vehicle that still scores a strong DriveClear Fit. Max is the most expensive a lender would realistically still work with — you can borrow it, but the monthly strain is real." },
      { q: "Is this a pre-approval?", a: "No. It's an educational estimate. Only a lender can approve a loan." },
    ],
    related: ["/how-much-car-can-i-afford", "/dti-calculator", "/car-payment-calculator"],
  },
  {
    path: "/how-much-car-can-i-afford",
    slug: "how-much-car-can-i-afford",
    title: "How Much Car Can I Afford? Start With Your Budget | DriveClear",
    description: "Answer 'how much car can I afford' starting from your monthly budget — see a comfortable payment and vehicle price based on your real income and obligations.",
    h1: "How Much Car Can I Afford?",
    intro: "The honest answer starts with your monthly budget, not the lot. Enter what you earn and owe, and see the payment — and price — that keeps your finances comfortable.",
    ctaLabel: "See What Fits",
    fields: [
      ...financeFields,
      { key: "creditScore", label: "Credit score range", type: "credit", def: "700" },
      { key: "down", label: "Down payment available", type: "currency", def: "5000" },
      { key: "term", label: "Preferred loan term", type: "term", def: "72" },
    ],
    compute: async (v) => {
      const { data } = await dcAffordability(affordabilityPayload(v));
      if (!data.comfortable_vehicle_price && !data.max_vehicle_price) {
        return {
          primary: { label: "Comfortable monthly budget", value: "$0" },
          tiles: [],
          note: "Your current obligations don't leave comfortable room for a car payment. Reducing monthly debt first will move this number fastest.",
        };
      }
      return {
        primary: { label: "Comfortable monthly payment", value: `${fmtMoney(data.comfortable_monthly_payment)}/mo` },
        tiles: [
          { label: "That buys roughly", value: fmtMoney(data.comfortable_vehicle_price) },
          { label: "Stretch ceiling", value: fmtMoney(data.max_vehicle_price) },
          { label: "Estimated APR", value: fmtPct(data.apr) },
        ],
        note: "Comfortable = the most expensive vehicle that still scores a strong DriveClear Fit. Stay near it and the car won't crowd out the rest of your life.",
      };
    },
    faqs: [
      { q: "Should I spend up to the max?", a: "The max is what a lender might allow, not what keeps your budget healthy. The comfortable figure leaves room for insurance, fuel, maintenance and life." },
      { q: "What if my number is lower than I hoped?", a: "A bigger down payment, less monthly debt, or a longer term each move the number. The DriveClear app shows which specific move helps your situation most." },
    ],
    related: ["/car-affordability-calculator", "/dti-calculator", "/car-cost-of-ownership"],
  },
  {
    path: "/dti-calculator",
    slug: "dti-calculator",
    title: "DTI Calculator for Car Loans — Debt-to-Income After a Car Payment | DriveClear",
    description: "See how a car payment changes your debt-to-income ratio — the number lenders watch. Compare your DTI before and after a new auto payment.",
    h1: "DTI Calculator for Car Buyers",
    intro: "Lenders watch your debt-to-income ratio (DTI): your monthly obligations divided by your income. Enter a proposed car payment and see exactly where it puts you.",
    ctaLabel: "Check My DTI",
    fields: [
      ...financeFields,
      { key: "payment", label: "Proposed car payment", type: "currency", def: "750", help: "Use the payment calculator first if you don't know it" },
    ],
    compute: async (v) => {
      const { data } = await dcDti({
        monthly_income: num(v.income),
        monthly_debts: num(v.debts),
        housing_cost: num(v.housing),
        proposed_payment: num(v.payment),
      });
      return {
        primary: { label: "DTI after this payment", value: fmtPct(data.dti_after_payment_pct) },
        tiles: [
          { label: "Estimated insurance", value: `${fmtMoney(data.estimated_insurance)}/mo` },
          { label: "Estimated maintenance", value: `${fmtMoney(data.estimated_maintenance)}/mo` },
        ],
        viz: {
          type: "dtiBars",
          bars: [
            { label: "Current DTI", value: data.current_dti_pct },
            { label: "With car payment", value: data.dti_after_payment_pct },
            { label: "All-in (ins + maint)", value: data.dti_after_all_in_pct },
          ],
        },
        note: "The all-in view adds estimated insurance and maintenance — that's the number the DriveClear Fit reacts to.",
      };
    },
    faqs: [
      { q: "What's a good DTI for a car loan?", a: "Every lender differs, but lower is always safer. What matters is whether the payment fits comfortably — the DriveClear Fit score weighs DTI alongside everything else." },
      { q: "Does DTI include utilities or groceries?", a: "No. DTI counts debt obligations: housing, loans, cards, and the proposed car payment. That's why DriveClear also shows the all-in view with insurance and maintenance." },
    ],
    related: ["/car-affordability-calculator", "/car-payment-calculator", "/car-cost-of-ownership"],
  },
  {
    path: "/car-cost-of-ownership",
    slug: "car-cost-of-ownership",
    title: "Car Cost of Ownership Calculator — Payment + Insurance + Fuel | DriveClear",
    description: "Look beyond the car payment: estimate insurance, maintenance and fuel to see the real monthly and five-year cost of owning a vehicle.",
    h1: "Cost of Ownership Calculator",
    intro: "The payment is only part of it. Insurance, maintenance and fuel turn a $750 payment into a much bigger monthly number — see the whole picture before you buy.",
    ctaLabel: "See the Real Cost",
    fields: [
      { key: "price", label: "Vehicle price", type: "currency", def: "45000" },
      { key: "down", label: "Down payment", type: "currency", def: "5000" },
      { key: "creditScore", label: "Credit score range", type: "credit", def: "700" },
      { key: "term", label: "Loan term", type: "term", def: "72" },
      { key: "body", label: "Vehicle type", type: "body", def: "SUV" },
      { key: "mpg", label: "Fuel economy (MPG)", type: "number", def: "24" },
      { key: "miles", label: "Miles per year", type: "number", def: "12000" },
      { key: "gas", label: "Gas price per gallon", type: "currency", def: "3.50" },
    ],
    compute: async (v) => {
      const { data } = await dcCostOfOwnership({
        vehicle_price: num(v.price),
        down_payment: num(v.down),
        credit_score: parseInt(v.creditScore, 10),
        term_months: parseInt(v.term, 10),
        body: v.body,
        mpg: num(v.mpg),
        miles_per_year: num(v.miles),
        gas_price_per_gallon: num(v.gas),
      });
      const m = data.monthly;
      return {
        primary: { label: "Real monthly cost", value: `${fmtMoney(m.total)}/mo` },
        tiles: [
          { label: "Annual total", value: fmtMoney(data.annual_total) },
          { label: "Five-year total", value: fmtMoney(data.five_year_total) },
        ],
        viz: {
          type: "ownershipBars",
          parts: [
            { label: "Loan payment", value: m.loan_payment },
            { label: "Insurance", value: m.insurance },
            { label: "Maintenance", value: m.maintenance },
            { label: "Fuel", value: m.fuel },
          ].filter((p) => p.value > 0),
        },
        note: `Assumptions: ${data.assumptions.mpg} MPG, ${Number(data.assumptions.miles_per_year).toLocaleString()} mi/yr, ${fmtMoney(data.assumptions.gas_price_per_gallon, 2)}/gal${data.assumptions.insurance_estimated ? ", insurance estimated by DriveClear" : ""}${data.assumptions.maintenance_estimated ? ", maintenance estimated by DriveClear" : ""}.`,
      };
    },
    faqs: [
      { q: "Why is my real monthly cost so much higher than the payment?", a: "The loan is only one line. Insurance scales with vehicle price and type, maintenance accumulates, and fuel depends on how much you drive. This calculator adds them together." },
      { q: "Are insurance and maintenance quotes?", a: "No — they're DriveClear estimates derived from the vehicle price and type. Your actual quotes will differ." },
    ],
    related: ["/car-payment-calculator", "/car-affordability-calculator", "/dti-calculator"],
  },
];

export const CALC_TITLES = Object.fromEntries(CALCULATOR_PAGES.map((p) => [p.path, p.h1]));
